import type { ConfigValue, ConfigValueType, PlatformConfigEntry } from "@lumora/bridge";
import { useState } from "react";

export type ParsedConfigInput = { ok: true; value: ConfigValue } | { ok: false; error: string };

/** Editor text -> typed value for a config entry. Input parsing only; the backend validates rules. */
export function parseConfigInput(valueType: ConfigValueType, text: string): ParsedConfigInput {
  switch (valueType) {
    case "int": {
      const t = text.trim();
      if (!/^-?\d+$/.test(t)) return { ok: false, error: "Enter a whole number." };
      return { ok: true, value: Number.parseInt(t, 10) };
    }
    case "bool":
      return { ok: true, value: text === "true" };
    case "strings":
      return {
        ok: true,
        value: text
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter((l) => l !== ""),
      };
    default:
      return { ok: true, value: text };
  }
}

/** Stored value -> editor text (inverse of parseConfigInput). */
export function configInputText(entry: Pick<PlatformConfigEntry, "value" | "valueType">): string {
  const v = entry.value;
  if (entry.valueType === "strings") return Array.isArray(v) ? v.join("\n") : "";
  if (v === null || v === undefined) return "";
  return String(v);
}

/** True when the editor text parses to something different from the stored value. */
export function isConfigDirty(entry: PlatformConfigEntry, text: string): boolean {
  const parsed = parseConfigInput(entry.valueType, text);
  if (!parsed.ok) return false;
  return JSON.stringify(parsed.value) !== JSON.stringify(entry.value);
}

export type ConfigEntryRowProps = {
  entry: PlatformConfigEntry;
  saving: boolean;
  error: string | null;
  onSave: (value: ConfigValue) => void | Promise<void>;
};

function formatDefault(v: ConfigValue): string {
  if (Array.isArray(v)) return v.length ? v.join(", ") : "(none)";
  if (v === null || v === "") return "(empty)";
  return String(v);
}

/** One platform setting: editor by valueType, default, description, restart badge, audit stamp. */
export function ConfigEntryRow({ entry, saving, error, onSave }: ConfigEntryRowProps) {
  const [text, setText] = useState(configInputText(entry));
  const parsed = parseConfigInput(entry.valueType, text);
  const dirty = isConfigDirty(entry, text);
  const id = `cfg-${entry.key}`;

  let editor;
  if (entry.valueType === "bool") {
    editor = (
      <input id={id} type="checkbox" checked={text === "true"} onChange={(e) => setText(e.target.checked ? "true" : "false")} />
    );
  } else if (entry.valueType === "enum") {
    editor = (
      <select id={id} value={text} onChange={(e) => setText(e.target.value)}>
        {(entry.options ?? []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  } else if (entry.valueType === "strings") {
    editor = <textarea id={id} rows={4} value={text} onChange={(e) => setText(e.target.value)} />;
  } else if (entry.valueType === "int") {
    editor = <input id={id} type="number" step="1" value={text} onChange={(e) => setText(e.target.value)} />;
  } else {
    editor = <input id={id} type="text" value={text} onChange={(e) => setText(e.target.value)} />;
  }

  return (
    <form
      className="platform-config-row"
      data-platform-config-row={entry.key}
      onSubmit={(e) => {
        e.preventDefault();
        if (parsed.ok) void onSave(parsed.value);
      }}
    >
      <div className="platform-config-head">
        <label htmlFor={id} className="platform-config-key">
          {entry.key}
        </label>
        {entry.restartRequired ? <span className="platform-config-badge">Restart required</span> : null}
      </div>
      <p className="muted platform-config-desc">{entry.description}</p>
      <div className="platform-config-editor">{editor}</div>
      <p className="muted platform-config-meta">
        Default: {formatDefault(entry.default)}
        {entry.updatedBy ? ` · Updated by ${entry.updatedBy}` : ""}
        {entry.updatedAt ? ` · ${entry.updatedAt}` : ""}
      </p>
      {!parsed.ok ? <p className="platform-config-error" role="alert">{parsed.error}</p> : null}
      {error ? <p className="platform-config-error" role="alert">{error}</p> : null}
      <button type="submit" disabled={!dirty || saving}>
        {saving ? "Saving…" : "Save"}
      </button>
    </form>
  );
}
